import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";

const API = "https://nightingale-intelligence-core.onrender.com";

function useApi(endpoint) {
  const { token } = useAuth();
  return useQuery({
    queryKey: [endpoint],
    queryFn: async () => {
      const res = await fetch(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("API error");
      return res.json();
    },
    enabled: !!token,
    refetchInterval: endpoint.includes("iss") ? 30000 : 300000,
    staleTime: endpoint.includes("iss") ? 25000 : 240000,
  });
}

function StatCard({ label, value, sub, color = "#00E5AA", icon }) {
  return (
    <div style={{
      background: "#0C1018", border: "1px solid #1A1F30",
      borderRadius: 10, padding: "18px 20px",
    }}>
      <div style={{ fontSize: 11, color: "#6B839E", fontFamily: "DM Mono, monospace",
        textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color, fontFamily: "DM Mono, monospace",
        lineHeight: 1 }}>
        {value ?? "—"}
      </div>
      {sub && <div style={{ fontSize: 12, color: "#6B839E", marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function ISSMap({ lat, lon }) {
  if (!lat || !lon) return null;
  
  // Pointing to your specific NASA Tile Proxy on Render
  const mapUrl = `hhttps://nightingale-intelligence-core.onrender.com/v1/hh/satellite-tile/4/${Math.floor(lon)}/${Math.floor(lat)}`;

  return (
    <div style={{ position: "relative", height: "400px", background: "#060910", borderRadius: 8, overflow: "hidden", border: "1px solid #1A1F30" }}>
       <div style={{ position: "absolute", inset: 0, opacity: 0.4, backgroundImage: `url(${mapUrl})`, backgroundSize: 'cover' }}></div>
       <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", color: "#00E5AA", fontFamily: "DM Mono, monospace", textAlign: "center" }}>
          [ LIVE SATELLITE TILE INGESTION ACTIVE ]<br/>
          <span style={{ fontSize: "10px", color: "#6B839E" }}>{lat.toFixed(2)}° / {lon.toFixed(2)}°</span>
       </div>
    </div>
  );
} //

function DebrisBar({ active, total }) {
  if (!active || !total) return null;
  const activePercent = (active / total) * 100;
  const debrisPercent = 100 - activePercent;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: "#00E5AA" }}>✓ Active: {active.toLocaleString()}</span>
        <span style={{ fontSize: 12, color: "#EF4444" }}>⚠ Debris: {(total - active).toLocaleString()}</span>
      </div>
      <div style={{ height: 12, background: "#1A1F30", borderRadius: 6, overflow: "hidden",
        display: "flex" }}>
        <div style={{ width: `${activePercent}%`, background: "#00E5AA", borderRadius: "6px 0 0 6px",
          transition: "width 1s ease" }}/>
        <div style={{ width: `${debrisPercent}%`, background: "#EF4444", borderRadius: "0 6px 6px 0" }}/>
      </div>
      <div style={{ fontSize: 11, color: "#6B839E", marginTop: 6, fontFamily: "DM Mono, monospace" }}>
        {debrisPercent.toFixed(1)}% of tracked objects are debris — travelling at ~28,000 km/h
      </div>
    </div>
  );
}

export default function Satellites() {
  const { data: summary, isLoading: summaryLoading } = useApi("/api/satellites/summary");
  const { data: iss, isLoading: issLoading } = useApi("/api/satellites/iss");
  const { data: envSats, isLoading: envLoading } = useApi("/api/satellites/environmental");
  const { data: debris } = useApi("/api/satellites/debris");

  const [issTime, setIssTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setIssTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, color: "#00E5AA", fontFamily: "DM Mono, monospace",
          letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
          🛰️ Space Intelligence
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: "#E4ECF7", margin: 0 }}>
          Satellite Dashboard
        </h1>
        <p style={{ color: "#6B839E", fontSize: 14, marginTop: 6 }}>
          Live orbital data from the US Space Surveillance Network via CelesTrak ·
          {" "}{summaryLoading ? "Loading..." : `${summary?.total_active?.toLocaleString() || "—"} active satellites tracked`}
        </p>
      </div>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 12, marginBottom: 24 }}>
        <StatCard
          label="Active Satellites"
          value={summaryLoading ? "..." : summary?.total_active?.toLocaleString()}
          sub="Currently in orbit"
          color="#00E5AA"
          icon="🛰️"
        />
        <StatCard
          label="Starlink"
          value={summaryLoading ? "..." : summary?.total_starlink?.toLocaleString()}
          sub="SpaceX constellation"
          color="#F7931A"
          icon="⭐"
        />
        <StatCard
          label="Weather Sats"
          value={summaryLoading ? "..." : summary?.total_weather?.toLocaleString()}
          sub="Feeding HH data"
          color="#60A5FA"
          icon="☁️"
        />
        <StatCard
          label="ISS Altitude"
          value={issLoading ? "..." : iss?.altitude_km ? `${iss.altitude_km}km` : "—"}
          sub="Above Earth's surface"
          color="#A78BFA"
          icon="🔭"
        />
        <StatCard
          label="ISS Speed"
          value={issLoading ? "..." : iss?.velocity_kmh ? `${iss.velocity_kmh.toLocaleString()}` : "—"}
          sub="km/h orbital velocity"
          color="#F472B6"
          icon="💨"
        />
      </div>

      {/* ISS Live Tracker */}
      <div style={{ background: "#0C1018", border: "1px solid #1A1F30", borderRadius: 10,
        padding: "20px", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: "#A78BFA", fontFamily: "DM Mono, monospace",
              letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
              🔴 Live Tracking
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "#E4ECF7", margin: 0 }}>
              International Space Station
            </h2>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "#6B839E", fontFamily: "DM Mono, monospace" }}>
              {issTime.toUTCString().slice(17, 25)} UTC
            </div>
            {iss && !iss.error && (
              <a href={iss.track_url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: "#00E5AA", textDecoration: "none" }}>
                Track in detail ↗
              </a>
            )}
          </div>
        </div>

        {issLoading ? (
          <div style={{ height: 200, background: "#060910", borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#6B839E", fontSize: 13 }}>Loading ISS position...</div>
        ) : iss?.error ? (
          <div style={{ color: "#6B839E", fontSize: 13, padding: 20 }}>
            ISS position unavailable — try again shortly
          </div>
        ) : (
          <>
            <ISSMap lat={iss?.latitude} lon={iss?.longitude} />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 10, marginTop: 14 }}>
              {[
                { l: "Latitude", v: `${iss?.latitude}°` },
                { l: "Longitude", v: `${iss?.longitude}°` },
                { l: "Altitude", v: `${iss?.altitude_km} km` },
                { l: "Velocity", v: `${iss?.velocity_kmh?.toLocaleString()} km/h` },
                { l: "Visibility", v: iss?.visibility },
              ].map(({ l, v }) => (
                <div key={l} style={{ background: "#060910", borderRadius: 6, padding: "10px 12px" }}>
                  <div style={{ fontSize: 10, color: "#6B839E", fontFamily: "DM Mono, monospace",
                    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{l}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#E4ECF7",
                    fontFamily: "DM Mono, monospace" }}>{v}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Space Debris */}
      {debris && !debris.error && (
        <div style={{ background: "#0C1018", border: "1px solid #1A1F30", borderRadius: 10,
          padding: "20px", marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "#EF4444", fontFamily: "DM Mono, monospace",
            letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
            ⚠️ Space Debris
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#E4ECF7", margin: "0 0 16px" }}>
            {debris.total_tracked_objects?.toLocaleString()} Tracked Objects
          </h2>
          <DebrisBar active={debris.active_satellites} total={debris.total_tracked_objects} />
          <p style={{ fontSize: 13, color: "#6B839E", marginTop: 14, lineHeight: 1.7 }}>
            Source: {debris.source}
          </p>
        </div>
      )}

      {/* Environmental Satellites */}
      <div style={{ background: "#0C1018", border: "1px solid #1A1F30", borderRadius: 10,
        padding: "20px" }}>
        <div style={{ fontSize: 11, color: "#60A5FA", fontFamily: "DM Mono, monospace",
          letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
          ☁️ Environmental Monitoring
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "#E4ECF7", margin: "0 0 6px" }}>
          Satellites Feeding High Horse Data
        </h2>
        <p style={{ fontSize: 13, color: "#6B839E", marginBottom: 18 }}>
          These satellites provide the environmental intelligence powering this platform
        </p>

        {envLoading ? (
          <div style={{ color: "#6B839E", fontSize: 13 }}>Loading satellite data...</div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {(envSats || []).filter(s => s.highlighted).map(sat => (
              <div key={sat.norad_id}
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px 80px",
                  gap: 12, padding: "12px 16px", background: "#060910",
                  borderRadius: 8, border: "1px solid #1A1F30", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#E4ECF7",
                    fontFamily: "DM Mono, monospace" }}>{sat.name}</div>
                  <div style={{ fontSize: 11, color: "#60A5FA", marginTop: 2 }}>{sat.agency}</div>
                </div>
                <div style={{ fontSize: 12, color: "#6B839E", lineHeight: 1.5 }}>{sat.role}</div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#E4ECF7",
                    fontFamily: "DM Mono, monospace" }}>
                    {sat.altitude_km ? `${sat.altitude_km}km` : "—"}
                  </div>
                  <div style={{ fontSize: 10, color: "#6B839E" }}>altitude</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#E4ECF7",
                    fontFamily: "DM Mono, monospace" }}>
                    {sat.inclination ? `${sat.inclination}°` : "—"}
                  </div>
                  <div style={{ fontSize: 10, color: "#6B839E" }}>inclination</div>
                </div>
              </div>
            ))}
            {/* Non-highlighted satellites collapsed */}
            {(envSats || []).filter(s => !s.highlighted).length > 0 && (
              <div style={{ fontSize: 12, color: "#6B839E", padding: "8px 16px",
                background: "#060910", borderRadius: 8, border: "1px solid #1A1F30" }}>
                + {(envSats || []).filter(s => !s.highlighted).length} additional weather satellites tracked
              </div>
            )}
          </div>
        )}
      </div>

      {/* Source note */}
      <div style={{ marginTop: 16, fontSize: 11, color: "#6B839E",
        fontFamily: "DM Mono, monospace", textAlign: "right" }}>
        Data: CelesTrak / US Space Surveillance Network · wheretheiss.at · No API key required
      </div>
    </div>
  );
}
